import { useTranslation } from 'react-i18next';
import AddExpenseCategoryItems from './AddExpenseCategoryItems';
import useGetExpenseCategoryItemsList from '@hooks/Finance/Expense/useGetExpenseCategoryItemsList';
import Pagination from '@components/Shared/Pagination/Pagination';
import { useCallback, useRef, useState } from 'react';
import Loading from '@components/Shared/Loader/Loading';

interface Props {
  expenseCategoryId?: string;
}

const ExpenseCategoryItems = ({ expenseCategoryId }: Props) => {
  const { t, i18n } = useTranslation();

  // ✅ item currently being edited (for unified form)
  const [editingItem, setEditingItem] = useState<any | null>(null);
  const formRef = useRef<HTMLDivElement | null>(null);

  const {
    ApExpenseCategoryItemsList: data,
    loading,
    error
  } = useGetExpenseCategoryItemsList({
    expenseCategoryId: expenseCategoryId ?? ''
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [dataPerPage] = useState(4);
  const indexOfLastData = currentPage * dataPerPage;
  const indexOfFirstData = indexOfLastData - dataPerPage;
  const currentData = data?.slice(indexOfFirstData, indexOfLastData);

  // console.log(data);

  const onEdit = useCallback((item: any) => {
    setEditingItem(item);

    // ✅ UX: scroll to form
    setTimeout(() => {
      formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 0);
  }, []);

  return (
    <>
      <Loading loading={loading} error={error}>
        <div className='alert alert-info d-flex align-items-center mb-3 border-0 shadow-sm'>
          <div className='alert-icon fs-20px me-3'>
            <i className='bi bi-info-circle-fill text-info'></i>
          </div>
          <div className='flex-1'>
            <div className='fw-bold fs-13px'>{i18n.language === 'ar' ? 'ملحوظة هامة' : 'Important Note'}</div>
            <div className='fs-11px opacity-8'>
              {i18n.language === 'ar'
                ? 'في حال عدم تحديد حساب لبند المصروف، سيتم استخدام حساب فئة المصروف الأب تلقائيًا.'
                : 'If no account is selected, the parent category account will be used automatically.'}
            </div>
          </div>
        </div>
        {/* ✅ Unified Add/Edit form */}
        <div ref={formRef}>
          <AddExpenseCategoryItems
            expenseCategoryId={expenseCategoryId}
            editingItem={editingItem}
            onCancelEdit={() => setEditingItem(null)}
          />
        </div>

        <div className='table-responsive'>
          <table className='table table-hover text-nowrap'>
            <thead className='bg-theme bg-opacity-30'>
              <tr>
                <th scope='col'>{t('Name')}</th>
                <th scope='col'>{t('Account')}</th>
                <th scope='col text-end'></th>
              </tr>
            </thead>

            <tbody>
              {currentData?.map(item => {
                const isEditingThisRow = editingItem?.id === item.id;

                return (
                  <tr key={item.id} className={isEditingThisRow ? 'table-active' : ''}>
                    <td className='align-middle'>{i18n.language === 'en' ? item.name : item.name2}</td>
                    <td className='align-middle'>{i18n.language === 'en' ? item.accountName : item.accountName2}</td>

                    <td className='text-end align-middle'>
                      {!isEditingThisRow ? (
                        <button type='button' className='btn btn-outline-theme ms-1' onClick={() => onEdit(item)}>
                          <i className='far fa-lg fa-fw fa-edit me-1'></i>
                          {t('Button.Edit')}
                        </button>
                      ) : (
                        <button type='button' className='btn me-1 btn-default' onClick={() => setEditingItem(null)}>
                          <i className='fa fa-power-off fa-fw me-1'></i> {t('Cancel')}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <Pagination
          filteredData={data}
          dataPerPage={dataPerPage}
          handlePagination={(pageNumber: any) => setCurrentPage(pageNumber)}
          currentPage={currentPage}
        />
      </Loading>
    </>
  );
};

export default ExpenseCategoryItems;
